import Skeleton from "@mui/material/Skeleton";
import dynamic from "next/dynamic";
import { useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { twMerge } from "tailwind-merge";

import { OverflowTooltip } from "@/components/overflow-tooltip";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useDeleteWatchlist } from "@/features/watchlist/services/delete-watchlist";
import {
  WatchlistData,
  useAllWatchlists,
} from "@/features/watchlist/services/get-all-watchlists";
import { useSwitchWatchlist } from "@/features/watchlist/services/switch-watchlist";
import { useLang } from "@/hooks/use-lang";

import { CrossIcon } from "ui/icon/shared";

const RemoveConfirmDialog = dynamic(() => import("./RemoveConfirmDialog"), {
  ssr: false,
});

export const WatchListMenu = ({
  setIsPopperOpen,
}: {
  setIsPopperOpen: (value: boolean) => void;
}) => {
  const { t } = useLang();
  const [hoverWatchlistIndex, setHoverWatchlistIndex] = useState(-1);
  const { data: watchlists, isLoading, isError } = useAllWatchlists();
  const { mutate: switchWatchlist } = useSwitchWatchlist();
  const { mutate: deleteWatchlist, isLoading: isDeleting } =
    useDeleteWatchlist();
  const { profile } = useAuth();

  const selectedWatchlistRef = useRef<WatchlistData | null>(null);
  const [isRemoveConfirmDialogOpen, setIsRemoveConfirmDialogOpen] =
    useState(false);

  const openRemoveConfirmDialog = (selectedWatchlist: WatchlistData) => {
    selectedWatchlistRef.current = selectedWatchlist;
    setIsRemoveConfirmDialogOpen(true);
  };

  const handleDeleteWatchlist = () => {
    if (!selectedWatchlistRef.current) return;

    deleteWatchlist(
      { watchlistId: selectedWatchlistRef.current.watchlistId },
      {
        onSuccess: () => {
          toast.success(t.layout.watchlist.removeWatchlistSuccessToast);
        },
        onError: () => {
          toast.error(t.layout.watchlist.removeWatchlistFailToast);
        },
        onSettled: () => {
          setIsRemoveConfirmDialogOpen(false);
          setIsPopperOpen(false);
        },
      }
    );
  };

  return (
    <div className="max-h-[450px] overflow-y-auto py-1" role="menu">
      {isLoading || isError ? (
        <div>
          <Skeleton className="ml-4 w-24" />
          <Skeleton className="ml-4 w-20" />
        </div>
      ) : (
        watchlists.map((watchlist, index) => {
          const disabled =
            (profile?.plan === "Free" && index > 0) || !watchlist.watchlistId;

          return disabled ? (
            <div
              key={watchlist.watchlistId}
              className="flex items-center justify-between break-words px-4 py-2 text-inactive-text-color"
            >
              <OverflowTooltip
                title={watchlist.watchlistName}
                placement="left"
                className="max-w-[70%] select-none text-sm font-normal"
              >
                {watchlist.watchlistName}
              </OverflowTooltip>
            </div>
          ) : (
            <div
              key={watchlist.watchlistId}
              role="menuitem"
              aria-label={`watchlist ${watchlist.watchlistName}`}
              className={twMerge(
                "flex cursor-pointer items-center justify-between break-words px-4 py-2 hover:bg-sky-50",
                watchlist.active && "bg-sky-100"
              )}
              onClick={() => {
                switchWatchlist(
                  { watchlistId: watchlist.watchlistId },
                  {
                    onError: () => {
                      toast.error(t.error.serverError);
                    },
                  }
                );
                setIsPopperOpen(false);
              }}
              onMouseEnter={() => setHoverWatchlistIndex(index)}
              onMouseLeave={() => setHoverWatchlistIndex(-1)}
            >
              <OverflowTooltip
                title={watchlist.watchlistName}
                placement="left"
                wrapperClassName="max-w-[70%] select-none text-sm font-normal"
              >
                {watchlist.watchlistName}
              </OverflowTooltip>
              {hoverWatchlistIndex === index && watchlists.length > 1 && (
                <CrossIcon
                  role="button"
                  aria-label="remove watchlist"
                  key={index}
                  onMouseEnter={() => setHoverWatchlistIndex(index)}
                  className="scale-[1.3] cursor-pointer rounded-sm fill-neutral-400 p-1 hover:bg-sky-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    openRemoveConfirmDialog(watchlist);
                  }}
                />
              )}
            </div>
          );
        })
      )}
      <RemoveConfirmDialog
        isOpen={isRemoveConfirmDialogOpen}
        watchlistName={selectedWatchlistRef.current?.watchlistName ?? ""}
        isDeleting={isDeleting}
        onCancel={() => setIsRemoveConfirmDialogOpen(false)}
        onConfirm={handleDeleteWatchlist}
      />
    </div>
  );
};
